import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, } from 'recharts';
import { isArray, groupBy } from 'lodash';
import LoadingCircle from 'components/ui/LoadingCircle';
import { getCarrierScoreMetric, getCarrierOnTimeMetric, getCarrierStopTimelinessMetric, getCarrierPODTimelinessMetric, getCarrierPODQualityMetric, getCarrierCompletionRateMetric, getCarrierAppUsageMetric, } from 'utils/MetricAPIUtils';
import CalendarIcon from '../../v2/icons/CalendarIcon';
const weeks = {
    ONE: 1,
    TWO: 2,
    SIX: 6,
};
const timeFilters = [
    {
        name: 'This week',
        value: weeks.ONE,
        localizedSlug: 'THIS_WEEK',
    },
    {
        name: '2 weeks ago',
        value: weeks.TWO,
        localizedSlug: '2_WEEKS_AGO',
    },
    {
        name: '6 weeks ago',
        value: weeks.SIX,
        localizedSlug: '6_WEEKS_AGO',
    },
];
const typeFilters = [
    {
        name: 'Overall Score',
        value: 'overall_score',
        localizedSlug: 'OVERALL_SCORE',
    },
    {
        name: 'On Time',
        value: 'on_time',
        localizedSlug: 'ON_TIME',
    },
    {
        name: 'Stop Timeliness',
        value: 'stop_timeliness',
        localizedSlug: 'STOP_TIMELINESS',
    },
    {
        name: 'POD Timeliness',
        value: 'pod_timeliness',
        localizedSlug: 'POD_TIMELINESS',
    },
    {
        name: 'POD Quality',
        value: 'pod_quality',
        localizedSlug: 'POD_QUALITY',
    },
    {
        name: 'Completion',
        value: 'completion',
        localizedSlug: 'COMPLETION',
    },
    {
        name: 'App Usage',
        value: 'app_usage',
        localizedSlug: 'APP_USAGE',
    },
];
const groupDataByWeek = (carrierData) => {
    if (!carrierData || !isArray(carrierData)) {
        return carrierData;
    }
    const final = [];
    const groupedData = groupBy(carrierData, (data) => moment(data.timestamp).week());
    Object.keys(groupedData).forEach((week) => {
        const totalScore = groupedData[week].reduce((mem, data) => {
            mem += data.score;
            return mem;
        }, 0);
        final.push({
            date: moment(groupedData[week][0].timestamp)
                .startOf('week')
                .format('ddd MM/DD'),
            score: totalScore / (groupedData[week].length * 100),
        });
    });
    return final;
};
const groupOverallScoreByDay = (carrierData) => {
    if (!carrierData || !isArray(carrierData)) {
        return carrierData;
    }
    const final = [];
    const groupedData = groupBy(carrierData, (unit) => moment(unit.timestamp).week());
    Object.keys(groupedData).forEach((week) => {
        const groupedWeekData = groupBy(groupedData[week], (unit) => moment(unit.timestamp).day());
        const data = Object.keys(groupedWeekData)
            .map((day) => groupedWeekData[day])
            .pop();
        const totalStars = data.reduce((mem, unit) => {
            if (unit.stars) {
                mem += unit.stars;
            }
            return mem;
        }, 0);
        const averageStars = totalStars / data.length / 5;
        final.push({
            timestamp: data[0].timestamp,
            score: averageStars,
            date: `${moment(data[0].timestamp).startOf('week').format('ddd MM/DD')}`,
        });
    });
    return final.sort((a, b) => a.timestamp - b.timestamp);
};
const SelectDarkMode = (props) => {
    const { items, onSelect, showIcon, value } = props;
    const current = value || items[0];
    const { t } = useTranslation();
    const [item, setItem] = useState(current);
    useEffect(() => {
        setItem(current);
    }, [value]);
    function handleSelect(e) {
        setItem(e.target.value);
        onSelect(e.target.value);
    }
    return (React.createElement("div", { className: "flex items-center w-48 mb-4 mt-4" },
        showIcon && (React.createElement("span", { className: "text-white mr-2" },
            React.createElement(CalendarIcon, null))),
        React.createElement("select", { className: "bg-gray-700 rounded form-select tracking-wider text-white ", onChange: handleSelect, value: item }, items.map((filter) => (React.createElement("option", { key: filter.value, value: filter.value, className: "text-sm" }, t(filter.localizedSlug)))))));
};
const LineGraph = (props) => {
    const { data } = props;
    return (React.createElement(AreaChart, { width: 900, height: 400, data: data, margin: {
            top: 10,
            right: 15,
            left: 0,
            bottom: 0,
        } },
        React.createElement(CartesianGrid, { strokeDasharray: "3 3", stroke: "#FFF" }),
        React.createElement(XAxis, { tick: { stroke: '#FFF' }, tickLine: { stroke: '#FFF' }, dataKey: "name", stroke: "#FFF" }),
        React.createElement(YAxis, { tick: { stroke: '#FFF' }, tickLine: { stroke: '#FFF' }, domain: [0, 100], stroke: "#FFF" }),
        React.createElement(Tooltip, null),
        React.createElement(Area, { type: "monotone", dataKey: "score", stroke: "#21B88E", fill: "#21B88E" })));
};
const CarrierPerformanceGraph = (props) => {
    const { t } = useTranslation();
    const { timeAgo, setDashboardTime } = props;
    const [timeFilter, setTimeFilter] = useState(weeks.SIX);
    const [typeFilter, setTypeFilter] = useState(typeFilters[0]);
    const [graphData, setGraphData] = useState([]);
    useEffect(() => {
        const fetchData = async () => {
            const mapGraphData = (unit) => {
                return {
                    name: unit.date,
                    score: (unit.score * 100).toFixed(2),
                };
            };
            resetData();
            const carrierData = await getMetrics();
            const dataGrouping = typeFilter.value === 'overall_score' || typeFilter === 'overall_score'
                ? groupOverallScoreByDay(carrierData)
                : groupDataByWeek(carrierData);
            const data = carrierData && isArray(carrierData)
                ? dataGrouping.map(mapGraphData)
                : [mapGraphData(carrierData)];
            setGraphData(data);
        };
        fetchData();
    }, [timeFilter, typeFilter, timeAgo]);
    const handleSelectTime = (value) => {
        setTimeFilter(parseInt(value, 10));
        setDashboardTime(parseInt(value, 10));
    };
    const handleSelectMetric = (value) => {
        setTypeFilter(value);
    };
    const resetData = () => {
        setGraphData([]);
    };
    const getMetrics = () => {
        let params = {};
        switch (timeAgo) {
            case 2:
                params = {
                    startDate: moment().subtract(2, 'week').startOf('day').toISOString(),
                    endDate: moment().subtract(1, 'day').endOf('day').toISOString(),
                    group: 'week',
                };
                break;
            case 6:
                params = {
                    startDate: moment().subtract(6, 'week').startOf('day').toISOString(),
                    endDate: moment().subtract(1, 'day').endOf('day').toISOString(),
                    group: 'week',
                };
                break;
            default:
                params = {
                    startDate: moment().startOf('week').startOf('day').toISOString(),
                    endDate: moment().subtract(1, 'day').endOf('day').toISOString(),
                    group: 'day',
                };
                break;
        }
        switch (typeFilter) {
            case 'on_time':
                return getCarrierOnTimeMetric(props.id, Object.assign(Object.assign({}, params), { delta: true }));
            case 'stop_timeliness':
                return getCarrierStopTimelinessMetric(props.id, params);
            case 'pod_timeliness':
                return getCarrierPODTimelinessMetric(props.id, params);
            case 'pod_quality':
                return getCarrierPODQualityMetric(props.id, params);
            case 'completion':
                return getCarrierCompletionRateMetric(props.id, params);
            case 'app_usage':
                return getCarrierAppUsageMetric(props.id, params);
            default:
                return getCarrierScoreMetric(props.id, Object.assign(Object.assign({}, params), { delta: true }));
        }
    };
    return (React.createElement("div", { className: "flex flex-col" },
        React.createElement("div", { className: "-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" },
            React.createElement("div", { className: "py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" },
                React.createElement("div", { className: "p-4 shadow overflow-hidden border-b border-gray-200 sm:rounded-lg bg-gray-700 flex flex-row justify-between" },
                    React.createElement("div", { className: "flex flex-col" },
                        React.createElement("div", null,
                            React.createElement("div", { className: "mb-8 text-left text-m font-medium text-white tracking-wider" }, t('PERFORMANCE')),
                            React.createElement(SelectDarkMode, { items: timeFilters, showIcon: true, onSelect: handleSelectTime, value: timeAgo })),
                        React.createElement("div", null,
                            React.createElement("span", { className: "mb-4 py-3 text-left text-xs font-medium text-white tracking-wider" },
                                t('SELECT_A_METRIC'),
                                ":"),
                            React.createElement(SelectDarkMode, { items: typeFilters, onSelect: handleSelectMetric }))),
                    React.createElement("div", { className: "flex m-auto", style: { minHeight: '420px' } }, graphData && graphData.length ? (React.createElement(LineGraph, { data: graphData })) : (React.createElement("div", { className: "flex-1 items-center justify-center pt-24" },
                        React.createElement(LoadingCircle, { text: t('LOADING_GRAPH_DATA'), color: "green", textColor: "white" })))))))));
};
export default CarrierPerformanceGraph;
